<template>
  <nav id="navigation" aria-label="Main" class="relative z-10">
    <button
      class="relative flex-c focus-a z-10"
      :aria-label="
        isOpen
          ? 'Close navigation menu'
          : 'Open navigation menu (opens slideout panel)'
      "
      aria-controls="main-navigation"
      data-gtm-hamburger-menu
      :aria-expanded="isOpen"
      @click="handleClick"
      @focus="handleFocus($event)"
    >
      <span
        v-if="!phone"
        class="block text-body m-2"
        v-html="isOpen ? 'Close' : 'Menu'"
      />
      <IconBase
        :icon="isOpen ? 'CloseIcon' : 'BurgerIcon'"
        :size="mobile ? '38px' : '45px'"
      />
    </button>

    <Transition name="slide-left">
      <div
        v-show="isOpen"
        id="main-navigation"
        class="f-cover h-full bg-accent-3-90"
        @click="isOpen = false"
      >
        <div class="absolute top-0 w-full header flex-a z-1" />
        <div
          class="absolute right-0 h-full w-full md:w-96 bg-white header-padding overflow-y scrollbar"
        >
          <div class="relative p-12">
            <RouterLink
              :to="{ name: 'level' }"
              class="inline-block uppercase text-accent-1 font-serif text-lg mb-6 focus-a"
              >Home</RouterLink
            >
            <h2
              class="flex-a uppercase text-accent-1 font-serif text-lg font-regular"
            >
              Explore for yourself
              <svg xmlns="http://www.w3.org/2000/svg" width="17" height="32" viewBox="0 0 17 32" v-bind:svg-inline="''" v-bind:class="'h-4 w-auto rotate-270 fill-current ml-5'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M16.614 31.748l.509-.509a.86.86 0 000-1.22L3.1 16.004 17.123 1.982a.86.86 0 000-1.22l-.509-.509a.86.86 0 00-1.22 0L.252 15.387a.86.86 0 000 1.22l15.142 15.134a.853.853 0 001.219.007z"/></svg>
            </h2>
            <img
              :src="thumbnail.src"
              :alt="thumbnail.alt"
              class="absolute right-0 pin-y mt-32 w-28 h-auto"
            />
            <ul class="p-4">
              <li
                v-for="category in categories"
                :key="category.slug"
                class="mb-4 last:mb-2"
              >
                <RouterLink
                  :to="{
                    name: 'campus-category',
                    params: { level_id: level, category_id: category.slug }
                  }"
                  class="text-secondary focus-a"
                  v-html="category.title"
                />
              </li>
            </ul>
            <RouterLink
              :to="{
                name: 'guided-intro',
                params: { level_id: level, tour_id: 'guided-tour_tour' }
              }"
              class="relative inline-block uppercase text-accent-1 font-serif text-lg mb-6 focus-a"
              v-html="`Start a guided tour`"
            />
            <ul class="my-6 relative">
              <li
                v-for="(cta, i) in ctas"
                :key="`${cta.href}-${i}`"
                class="mb-3"
              >
                <Button
                  type="external"
                  :text="cta.call_to_action"
                  :aria="`cta.call_to_action`"
                  :path="cta.link"
                  :utm="`tier-1-cta`"
                  class="btn btn-primary"
                />
              </li>
            </ul>
            <p class="text-sm relative">
              Map illustration by BA (Hons) Illustration graduate,
              <a
                href="https://www.victoriaroseball.com"
                class="focus-a underline uppercase"
                >Victoria Rose Ball</a
              >
            </p>
            <p class="text-sm">
              Designed & developed by
              <a
                href="https://www.revolutionviewing.com"
                class="focus-a underline uppercase"
                >RV</a
              >
            </p>
          </div>
          <div class="bg-body">
            <h2
              class="bg-black text-grey-3 leading-none px-4 py-5 font-regular uppercase text-2xl font-serif"
            >
              The University of Edinburgh
            </h2>
            <div class="p-4">
              <ul class="text-grey-1 text-sm">
                <li
                  v-for="item in options.policy_statement_links"
                  :key="item.link"
                  class="mb-2"
                >
                  <a
                    :href="utmHref(item.link, 'tier-2-cta')"
                    :aria-label="`${item.link_copy} (opens in new window)`"
                    class="hover:underline hover:font-medium focus:underline focus:font-medium focus-a"
                    v-html="item.link_copy"
                  />
                </li>
              </ul>
              <a
                :href="
                  utmHref(
                    'https://www.ed.ac.uk/about/mission-governance/affiliations',
                    'tier-2-cta'
                  )
                "
                aria-label="University affiliations (opens in new window)"
                class="inline-block my-4 focus-a"
                @focus="handleFocus($event)"
              >
                <img
                  src="@/assets/footer-affiliations.png"
                  alt="University affiliations"
                  class="w-44 h-auto"
                />
              </a>
            </div>
            <p class="text-grey-1 text-sm p-4 mb-0">
              The University of Edinburgh is a charitable body, registered in
              Scotland, with registration number SC005336, VAT Registration
              Number GB 592 9507 00, and is acknowledged by the UK authorities
              as a “Recognised body” which has been granted degree awarding
              powers.
            </p>
            <p class="bg-white p-4 text-sm mb-0">
              Unless explicitly stated otherwise, all material is copyright ©
              The University of Edinburgh 2020.
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </nav>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import IconBase from '@/components/icons/IconBase.vue'
import Button from '@/components/buttons/Button.vue'

export default {
  name: 'Navigation',

  components: {
    IconBase,
    Button
  },

  data() {
    return {
      isOpen: false
    }
  },

  computed: {
    ...mapState(['touch', 'mobile', 'phone']),
    ...mapGetters(['options', 'categories', 'level', 'ctas']),

    thumbnail() {
      if (this.options.navigation_thumbnail)
        return {
          src: this.options.navigation_thumbnail.url,
          alt: this.options.navigation_thumbnail.alt
        }
      return ''
    }
  },

  methods: {
    handleClick() {
      this.isOpen = !this.isOpen
      this.$store.commit('toggleNavState')
    },

    handleFocus() {
      /*
       * Traps focus within the menu. Make sure to add the
       * focus handle on the first and last focusable elements
       */
      const domNode = document.getElementById('navigation')
      const focusableEls = [...domNode.querySelectorAll('button, a')]
      const firstFocusableEl = focusableEls[0]
      const lastFocusableEl = focusableEls[focusableEls.length - 1]

      event.target.addEventListener('keydown', e => {
        const isTabPressed = e.code === 'Tab'

        if (!isTabPressed) {
          return
        }

        if (e.shiftKey) {
          if (document.activeElement === firstFocusableEl && this.isOpen) {
            lastFocusableEl.focus()
            e.preventDefault()
          }
        } else {
          if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus()
            e.preventDefault()
          }
        }
      })
    }
  }
}
</script>
